import React, { Fragment } from 'react'
import styled, { css } from 'styled-components';
import { colors2 } from 'oolib';
import { NoOptionResultsComp } from './comps/NoOptionResultsComp';
import { ListItem } from './comps/ListItem';
import { SearchBar } from './comps/SearchBar';
import { ListGroup } from './comps/ListGroup';
import { useDragSelect } from './hooks/useDragSelect';

export const StyledListWrapper = styled.div`
  background: white;
  border-radius: 0.6rem;
  padding: 0.2rem;
  padding-top: ${({ isSearchable }) => isSearchable ? "0rem" : "0.2rem"};

  ${({ disableShadow }) =>
    !disableShadow &&
    css`
      box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
    `}

  ${({ enableBorder }) =>
    enableBorder &&
    css`
      border: 1px solid ${colors2.grey10};
    `}
`;


export const isGroupedData = (options) => {
  // If the data is empty or not an array, return false
  if (!options || !Array.isArray(options) || options.length === 0) {
    return false;
  }

  // Check the first item in the array
  // If it has an 'options' property that is an array, it's grouped data
  return ( Array.isArray(options) && options.every(op => !!op?.options && Array.isArray(op.options)) );
};

export const List = ({
  id,
  options = [],
  value,
  focussedOp,
  scrollFocussedOpIntoView,
  onChange: parentOnChange,
  optionsClassName,
  invert,
  S,
  observerRef,
  isMulti = false,
  isTagsStyle,
  setFocusSelectTagsInput,
  disableShadow,
  isSearchable,
  searchbarPlaceholder,
  searchBarFocus,
  searchString,
  setSearchString,
  setSearchBarFocus,
  children,
  enableBorder,
  style,
  groupHeaderStickyTop,
  showList: showListInParent,
  usedIn,
  alignCheckbox,
  iconAfter,
  dragSelectAcrossGroups = false // When true, drag selection can span across groups
}) => {
  const {
    optimisticSelections,
    handleDragStart,
    handleDragOver,
    handleItemChange
  } = useDragSelect({
    options,
    value,
    id,
    isMulti,
    isTagsStyle,
    dragSelectAcrossGroups,
    parentOnChange,
    setSearchString,
    setSearchBarFocus,
    setFocusSelectTagsInput
  });

  const renderListItems = () => {
    if (options.length === 0) {
      return <NoOptionResultsComp S={S} />;
    }

    if (isGroupedData(options)) {
      // Calculate index offset for each group (for drag selection)
      let indexOffset = 0;
      return (
        <Fragment>
          {options.map((group, groupIndex) => {
            const groupStartIndex = indexOffset;
            indexOffset += group.options.length;
            return (
              <ListGroup
                id={id}
                key={`group-${groupIndex}`}
                group={group}
                index={groupIndex}
                groupStartIndex={groupStartIndex}
                value={value}
                focussedOp={focussedOp}
                scrollFocussedOpIntoView={scrollFocussedOpIntoView}
                optionsClassName={optionsClassName}
                invert={invert}
                S={S}
                observerRef={observerRef}
                isMulti={isMulti}
                isTagsStyle={isTagsStyle}
                groupHeaderStickyTop={groupHeaderStickyTop}
                alignCheckbox={alignCheckbox}
                iconAfter={iconAfter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                optimisticSelections={optimisticSelections}
                onChange={handleItemChange}
              />
            );
          })}
        </Fragment>
      );
    }

    return options.map((option, index) => (
      <ListItem
        key={option.value}
        option={option}
        isSelected={(!option.loading && value?.value === option.value)}
        isFocussed={focussedOp === index}
        index={index}
        scrollFocussedOpIntoView={scrollFocussedOpIntoView}
        optionsClassName={optionsClassName}
        invert={invert}
        S={S}
        observerRef={options.length === index + 1 ? observerRef : null}
        isMulti={isMulti}
        isTagsStyle={isTagsStyle}
        value={value}
        alignCheckbox={alignCheckbox}
        iconAfter={iconAfter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        optimisticSelections={optimisticSelections}
        onChange={handleItemChange}
      />
    ));
  };

  return (
    <StyledListWrapper disableShadow={disableShadow} style={style} isSearchable={isSearchable} enableBorder={enableBorder}>
      {isSearchable && (
        <SearchBar
          searchbarPlaceholder={searchbarPlaceholder}
          searchString={searchString}
          setSearchString={setSearchString}
          setSearchBarFocus={setSearchBarFocus}

          // In dropdowns searchBarFocus is only called from search bar component when dropdowns is opened
          // because in Dropdown  - List and searchBar is present in DOM even when the dropdown is closed
          searchBarFocus={usedIn !== "dropdown" ? searchBarFocus : usedIn === "dropdown" && showListInParent ? searchBarFocus : false}
        />
      )}
      {children} {/* For now it is used for selected list */}
      {renderListItems()}
    </StyledListWrapper>
  );
};
